import type { LegalCommon, LegalDocument } from "@/components/LegalPageLayout";

const legalEyebrow = "Pravno";
const updated = "Zadnja posodobitev: 1. avgust 2026";

/**
 * Slovenian is the authoritative version of these documents (see the closing
 * clause of the Terms). en/de/ru are translations and must be kept in step with
 * this file — the i18n `Translations` type enforces the key structure, not the
 * content.
 */
export const legalSl = {
  common: {
    tocHeading: "Vsebina",
    company: {
      legalName: "Naziv",
      address: "Sedež",
      registrationNumber: "Matična številka",
      vatNumber: "ID za DDV",
      taxNumber: "Davčna številka",
      registryEntry: "Vpis v register",
      shareCapital: "Osnovni kapital",
      representative: "Zastopnik",
      email: "E-pošta",
      phone: "Telefon",
      website: "Spletno mesto",
    },
  } satisfies LegalCommon,

  // ---------------------------------------------------------------------------
  // Splošni pogoji
  // ---------------------------------------------------------------------------
  terms: {
    eyebrow: legalEyebrow,
    title: "Splošni pogoji poslovanja in uporabe",
    updated,
    intro:
      "Ti splošni pogoji urejajo uporabo spletnega mesta nreport.ai in naročanje poročil o nepremičninah. Spletno mesto in storitev upravlja družba Noventis d.o.o. Prosimo, da pogoje pozorno preberete — z uporabo storitve potrjujete, da ste jih prebrali in se z njimi strinjate.",
    sections: [
      {
        title: "1. Podatki o ponudniku",
        blocks: [
          {
            type: "p",
            text: "Spletno mesto nreport.ai in storitev nReport upravlja in izvaja:",
          },
          { type: "company" },
          {
            type: "p",
            text: "Podatki so objavljeni v skladu s 6. členom Zakona o elektronskem poslovanju na trgu (ZEPT) in 45. členom Zakona o gospodarskih družbah (ZGD-1). V nadaljevanju se družba Noventis d.o.o. imenuje »ponudnik«, »mi« ali »nReport«, oseba, ki uporablja storitev, pa »uporabnik« ali »vi«.",
          },
        ],
      },
      {
        title: "2. Področje uporabe in sprejem pogojev",
        blocks: [
          {
            type: "p",
            text: "Pogoji veljajo za vse obiskovalce spletnega mesta, registrirane uporabnike in kupce poročil, tako potrošnike kot poslovne subjekte. Pogoji so sestavni del vsake pogodbe, sklenjene prek spletnega mesta.",
          },
          {
            type: "p",
            text: "Pogoje sprejmete ob oddaji naročila. Pred zaključkom nakupa so vam pogoji vedno dostopni v obliki, ki jo lahko shranite in natisnete. Če se s pogoji ne strinjate, storitve ne smete uporabljati.",
          },
          {
            type: "p",
            text: "Potrošnik je fizična oseba, ki pridobiva ali uporablja storitev za namene izven svoje poklicne ali pridobitne dejavnosti. Določbe, ki so izrecno označene kot potrošniške, veljajo samo za potrošnike; kogentne določbe Zakona o varstvu potrošnikov (ZVPot-1) veljajo ne glede na te pogoje in jih ti pogoji ne omejujejo.",
          },
        ],
      },
      {
        title: "3. Opis storitve",
        blocks: [
          {
            type: "p",
            text: "nReport je spletna storitev, ki za posamezno nepremičnino v Republiki Sloveniji samodejno zbere podatke iz javno dostopnih uradnih evidenc in javno objavljenih oglasov, jih obdela ter izdela strukturirano poročilo v obliki dokumenta PDF.",
          },
          { type: "p", text: "Poročilo praviloma vsebuje:" },
          {
            type: "ul",
            items: [
              "podatke iz katastra nepremičnin in registra nepremičnin (GURS),",
              "podatke o realiziranih poslih iz Evidence trga nepremičnin (ETN),",
              "informacijo o stanju v zemljiški knjigi, kadar je izpisek dosegljiv,",
              "podatke o energetski izkaznici, kadar so na voljo,",
              "pregled primerljivih javno objavljenih oglasov,",
              "opis lokacije in okolice,",
              "okvirno oceno tržne vrednosti in besedilno analizo, izdelano s pomočjo umetne inteligence.",
            ],
          },
          {
            type: "p",
            text: "Natančna vsebina poročila je odvisna od razpoložljivosti podatkov za konkretno nepremičnino. Če posamezen vir podatkov ni dosegljiv ali za nepremičnino ne vsebuje zapisa, je ta del poročila lahko okrnjen ali izpuščen; to ne pomeni napake v izpolnitvi.",
          },
        ],
      },
      {
        title: "4. Narava poročila — kaj poročilo ni",
        blocks: [
          {
            type: "note",
            text: "Poročilo nReport je informativni pripomoček. Ni uradna cenitev in ni ocena vrednosti nepremičnine, izdelana po Zakonu o revidiranju (ZRev-2) in Mednarodnih standardih ocenjevanja vrednosti. Ne izdela ga pooblaščeni ocenjevalec vrednosti nepremičnin in ne more nadomestiti njegove ocene.",
          },
          { type: "p", text: "Poročilo prav tako ni:" },
          {
            type: "ul",
            items: [
              "pravni, davčni, računovodski ali naložbeni nasvet,",
              "nadomestilo za uradni zemljiškoknjižni izpisek ali overjen izpisek iz katastra,",
              "nadomestilo za pregled nepremičnine na kraju samem, za gradbeno-tehnični pregled ali za pravni skrbni pregled,",
              "podlaga za odločitev kreditodajalca, zavarovalnice ali državnega organa,",
              "dokazilo v sodnem, davčnem ali upravnem postopku.",
            ],
          },
          {
            type: "p",
            text: "Pred sklenitvijo posla z nepremičnino vam priporočamo, da pridobite uradne izpiske iz zemljiške knjige in katastra ter se posvetujete z odvetnikom, notarjem, nepremičninskim posrednikom oziroma pooblaščenim ocenjevalcem vrednosti nepremičnin.",
          },
        ],
      },
      {
        title: "5. Uporaba umetne inteligence",
        blocks: [
          {
            type: "p",
            text: "Besedilni del poročila in ocena vrednosti nastaneta samodejno, s pomočjo sistemov umetne inteligence (veliki jezikovni modeli) in statističnih modelov. Vsebine pred izročitvijo praviloma ne pregleda človek.",
          },
          {
            type: "p",
            text: "Sistemi umetne inteligence lahko kljub skrbni zasnovi ustvarijo netočno, nepopolno ali zavajajočo trditev, tudi kadar so vhodni podatki pravilni. Vsako navedbo v poročilu, na katero se nameravate opreti pri odločitvi, preverite v izvornem uradnem viru.",
          },
          {
            type: "p",
            text: "Podatkov, ki jih vnesete v storitev, in vsebine izdelanih poročil ne uporabljamo za učenje modelov umetne inteligence, prav tako to pogodbeno prepovedujemo ponudnikom modelov, ki jih uporabljamo.",
          },
        ],
      },
      {
        title: "6. Viri podatkov in njihova ažurnost",
        blocks: [
          {
            type: "p",
            text: "Poročilo temelji na podatkih iz javno dostopnih virov, med drugim iz evidenc Geodetske uprave Republike Slovenije (kataster nepremičnin, register nepremičnin, Evidenca trga nepremičnin), iz zemljiške knjige, iz registra energetskih izkaznic, iz kartografskih virov ter iz javno objavljenih nepremičninskih oglasov.",
          },
          {
            type: "p",
            text: "Ponudnik ni izvor teh podatkov in nima vpliva na njihovo pravilnost, popolnost ali ažurnost. Uradne evidence se posodabljajo z zamikom, podatki v njih pa lahko odstopajo od dejanskega stanja nepremičnine (na primer zaradi neprijavljenih posegov, neusklajenih vpisov ali napak v izvorni evidenci). Podatki, prevzeti iz oglasov, izražajo ceno, ki jo zahteva oglaševalec, in ne dosežene prodajne cene.",
          },
          {
            type: "p",
            text: "Vsi podatki v poročilu se nanašajo na trenutek izdelave poročila. Poročilo se po izdelavi ne posodablja samodejno.",
          },
        ],
      },
      {
        title: "7. Uporabniški račun",
        blocks: [
          {
            type: "ul",
            items: [
              "Za dostop do naročenih poročil potrebujete uporabniški račun z veljavnim elektronskim naslovom. Račun lahko ustvarite z geslom ali prek prijave s ponudnikom Google oziroma Facebook.",
              "Ob nakupu brez predhodne registracije vam račun ustvarimo samodejno na elektronski naslov, ki ga navedete pri plačilu, in vam pošljemo povezavo za prijavo.",
              "Podatki, ki jih navedete, morajo biti resnični in ažurni.",
              "Za varovanje gesla in za vso uporabo storitve prek svojega računa odgovarjate sami. O sumu nepooblaščene uporabe nas nemudoma obvestite.",
              "Račun je namenjen eni osebi oziroma enemu poslovnemu subjektu in ga ni dovoljeno prenašati ali dajati v souporabo.",
              "Storitev ni namenjena osebam, mlajšim od 18 let.",
              "Račun lahko kadar koli izbrišete; glejte razdelek o brisanju podatkov v Politiki zasebnosti.",
            ],
          },
        ],
      },
      {
        title: "8. Krediti, cene in plačilo",
        blocks: [
          {
            type: "ul",
            items: [
              "Poročila se plačujejo s krediti. En kredit omogoča izdelavo in odklep enega celotnega poročila za eno nepremičnino.",
              "Cene so objavljene na spletnem mestu in so v evrih (EUR). Prikazana cena je končna cena, ki jo plačate. Ponudnik ni zavezanec za DDV, zato DDV ni obračunan na podlagi 1. odstavka 94. člena ZDDV-1.",
              "Dodatnih stroškov dostave ni, ker se poročilo izroči v elektronski obliki.",
              "Plačilo se izvede vnaprej, prek ponudnika plačilnih storitev Stripe. Ponudnik ne prejme in ne hrani podatkov o vaši plačilni kartici.",
              "Račun izdamo v elektronski obliki in ga pošljemo na vaš elektronski naslov. Z oddajo naročila soglašate z izdajo računa v elektronski obliki.",
              "Kupljeni krediti so veljavni 1 leto od dneva nakupa, kot je navedeno v ceniku. Neporabljenih kreditov po izteku veljavnosti ne nadomeščamo, razen če se dogovorimo drugače.",
              "Krediti se ne izplačujejo v denarju, razen v primerih, ki jih določa zakon ali ti pogoji (na primer pri odstopu od pogodbe).",
            ],
          },
        ],
      },
      {
        title: "9. Sklenitev pogodbe in izvedba",
        blocks: [
          {
            type: "p",
            text: "Prikaz storitve na spletnem mestu je vabilo k oddaji ponudbe. Pogodba je sklenjena, ko oddate naročilo s klikom na gumb, ki izrecno navaja obveznost plačila, in ko je plačilo uspešno potrjeno. Potrditev naročila vam pošljemo na elektronski naslov skupaj s temi pogoji na trajnem nosilcu podatkov.",
          },
          {
            type: "p",
            text: "Izdelava poročila se začne takoj po potrditvi plačila oziroma takoj po tem, ko za izbrano nepremičnino porabite kredit. Poročilo je praviloma na voljo v nekaj minutah; kadar je treba pridobiti podatke iz počasnih ali začasno nedosegljivih zunanjih evidenc, lahko izdelava traja dlje, najpozneje pa je poročilo izdelano v 48 urah. Poročilo je dostopno v vašem uporabniškem računu, kopijo pa prejmete tudi po elektronski pošti.",
          },
          {
            type: "p",
            text: "Če poročila zaradi razlogov na naši strani ni mogoče izdelati, vam kredit vrnemo na račun oziroma, na vašo zahtevo, povrnemo plačano ceno.",
          },
        ],
      },
      {
        title: "10. Pravica potrošnika do odstopa od pogodbe",
        blocks: [
          {
            type: "p",
            text: "Potrošnik ima v skladu s 134. členom ZVPot-1 pravico, da v 14 dneh od sklenitve pogodbe odstopi od pogodbe, sklenjene na daljavo, ne da bi za to navedel razlog. Za odstop zadostuje nedvoumna izjava, ki jo pošljete na info@nreport.ai; uporabite lahko tudi obrazec za odstop iz Pravilnika o obrazcu za odstop.",
          },
          {
            type: "p",
            text: "Za nakup kreditov to pomeni: neporabljene kredite vam v 14 dneh brez pojasnila v celoti povrnemo. Vračilo izvedemo z istim plačilnim sredstvom, kot je bilo uporabljeno za plačilo, najpozneje v 14 dneh od prejema izjave o odstopu.",
          },
          {
            type: "note",
            text: "Izjema za že izdelano poročilo: v skladu s 13. točko prvega odstavka 135. člena ZVPot-1 potrošnik nima pravice do odstopa pri dobavi digitalne vsebine, ki se ne dostavi na otipljivem nosilcu podatkov, če se je izvajanje začelo z izrecnim predhodnim soglasjem potrošnika in ob njegovi privolitvi, da s tem izgubi pravico do odstopa. Pred plačilom vas zato izrecno prosimo za to soglasje. Ko je poročilo za izbrano nepremičnino izdelano in vam dano na voljo, pravice do odstopa za porabljeni kredit ni več.",
          },
          {
            type: "p",
            text: "Če soglasja ne daste, nakupa ni mogoče izvesti prek spletnega mesta; v tem primeru nas kontaktirajte na info@nreport.ai in se bomo dogovorili za drugačen način izvedbe.",
          },
          {
            type: "p",
            text: "Pravica do odstopa ne velja za pravne osebe, samostojne podjetnike in druge osebe, ki storitev naročijo v okviru svoje dejavnosti.",
          },
        ],
      },
      {
        title: "11. Skladnost digitalne vsebine in jamčevalni zahtevki",
        blocks: [
          {
            type: "p",
            text: "Zavezujemo se dobaviti poročilo, ki je skladno s pogodbo v smislu določb ZVPot-1 o digitalni vsebini in digitalnih storitvah: ustreza opisu iz teh pogojev in s spletnega mesta, je primerno za običajen namen takšne vsebine in je dobavljeno v dogovorjenem obsegu.",
          },
          {
            type: "p",
            text: "Če poročilo ni skladno s pogodbo, lahko potrošnik zahteva vzpostavitev skladnosti (ponovno izdelavo ali dopolnitev poročila), znižanje kupnine ali odstop od pogodbe pod pogoji, ki jih določa ZVPot-1. Neskladnost sporočite na info@nreport.ai in ji priložite opis pomanjkljivosti. Na zahtevek odgovorimo najpozneje v 8 dneh.",
          },
          {
            type: "p",
            text: "Odstopanje podatkov iz uradnih evidenc od dejanskega stanja nepremičnine ni neskladnost poročila, saj ponudnik teh podatkov ne ustvarja in zanje ne jamči — glej 6. in 12. razdelek.",
          },
        ],
      },
      {
        title: "12. Omejitev odgovornosti",
        blocks: [
          {
            type: "p",
            text: "Storitev je zasnovana kot informativni pripomoček. Ponudnik ne jamči za pravilnost, popolnost ali ažurnost podatkov, prevzetih iz zunanjih virov, niti za točnost ocene vrednosti ali besedilnih analiz, ustvarjenih s pomočjo umetne inteligence.",
          },
          {
            type: "p",
            text: "Uporabnik se z uporabo storitve strinja, da odločitev o nakupu, prodaji, najemu, financiranju ali vrednotenju nepremičnine ne bo oprl izključno na poročilo. Ponudnik ne odgovarja za poslovne odločitve, sprejete na podlagi poročila, niti za izgubljeni dobiček, izgubo priložnosti ali drugo posredno oziroma posledično škodo.",
          },
          {
            type: "p",
            text: "Skupna odgovornost ponudnika iz posameznega naročila je omejena na znesek, ki ga je uporabnik za to naročilo plačal, razen če prisilni predpisi določajo drugače.",
          },
          {
            type: "note",
            text: "Nobena določba teh pogojev ne izključuje in ne omejuje odgovornosti ponudnika za naklep ali hudo malomarnost, za škodo, povzročeno na življenju ali zdravju, niti zakonskih pravic potrošnika po ZVPot-1 in Obligacijskem zakoniku.",
          },
          {
            type: "p",
            text: "Storitev je na voljo »takšna, kot je«. Ponudnik si prizadeva za neprekinjeno delovanje, vendar ne jamči, da bo storitev vedno dosegljiva brez napak in prekinitev; načrtovana vzdrževalna dela in izpadi zunanjih virov podatkov so pričakovani.",
          },
        ],
      },
      {
        title: "13. Dovoljena uporaba in prepovedi",
        blocks: [
          { type: "p", text: "Pri uporabi storitve ni dovoljeno:" },
          {
            type: "ul",
            items: [
              "sistematično, samodejno ali množično zajemati vsebin spletnega mesta (na primer s streženjem, roboti ali podatkovnim rudarjenjem),",
              "poskušati pridobiti dostop do delov sistema, do katerih nimate pravice, ali obiti varnostnih mehanizmov,",
              "preobremenjevati infrastrukturo ali motiti delovanje storitve,",
              "storitev uporabljati za nadlegovanje, zalezovanje ali profiliranje posameznikov,",
              "naročati poročil za nepremičnine, kadar za to nimate zakonitega interesa,",
              "poročila ali njihove dele preprodajati, distribuirati, javno objavljati ali vključevati v lastne izdelke brez našega predhodnega pisnega dovoljenja,",
              "odstranjevati ali prikrivati oznak o avtorstvu, virih podatkov in opozoril iz poročila.",
            ],
          },
          {
            type: "p",
            text: "Ob kršitvi lahko dostop do storitve začasno omejimo ali ukinemo, pri hujših ali ponavljajočih se kršitvah pa uporabniški račun ukinemo. O ukrepu vas obvestimo in vam, kadar je to mogoče, damo priložnost, da kršitev odpravite.",
          },
        ],
      },
      {
        title: "14. Intelektualna lastnina in licenca za uporabo poročila",
        blocks: [
          {
            type: "p",
            text: "Spletno mesto, njegova zasnova, programska koda, besedila, grafika, znamka nReport ter struktura, izbor in predstavitev podatkov v poročilih so avtorskopravno varovani oziroma varovani kot pravica izdelovalca podatkovne baze (Zakon o avtorski in sorodnih pravicah). Vse pravice so pridržane.",
          },
          {
            type: "p",
            text: "Z nakupom poročila pridobite neizključno, neprenosljivo in časovno neomejeno pravico, da poročilo uporabljate za lastne potrebe, vključno s tem, da ga predložite svojim svetovalcem, banki, nepremičninskemu posredniku ali pogodbeni stranki v konkretnem poslu. Vsaka širša uporaba, zlasti javna objava, preprodaja ali sistematično razmnoževanje, je dopustna le z našim predhodnim pisnim dovoljenjem.",
          },
          {
            type: "p",
            text: "Podatki iz uradnih javnih evidenc so last oziroma v upravljanju pristojnih organov in so v poročilu uporabljeni kot informacija o javno dostopnem stanju; pravice teh organov s tem niso prenesene na uporabnika.",
          },
        ],
      },
      {
        title: "15. Deljenje poročila prek povezave",
        blocks: [
          {
            type: "p",
            text: "Storitev omogoča, da za svoje poročilo ustvarite povezavo za deljenje. Vsakdo, ki povezavo pozna, si lahko poročilo ogleda brez prijave, zato jo delite premišljeno. Povezavo lahko kadar koli prekličete v svojem uporabniškem računu; po preklicu poročilo prek nje ni več dosegljivo.",
          },
          {
            type: "p",
            text: "Za vsebino, ki jo delite, in za posledice deljenja odgovarjate sami.",
          },
        ],
      },
      {
        title: "16. Varstvo osebnih podatkov",
        blocks: [
          {
            type: "p",
            text: "Osebne podatke obdelujemo v skladu s Splošno uredbo o varstvu podatkov (GDPR) in Zakonom o varstvu osebnih podatkov (ZVOP-2). Podrobnosti o vrstah podatkov, namenih, pravnih podlagah, rokih hrambe, obdelovalcih in vaših pravicah so v Politiki zasebnosti, ki je sestavni del teh pogojev.",
          },
        ],
      },
      {
        title: "17. Pritožbe in izvensodno reševanje sporov",
        blocks: [
          {
            type: "p",
            text: "Pritožbo lahko pošljete na info@nreport.ai. Prejem pritožbe potrdimo v 5 delovnih dneh, o utemeljenosti pa vas obvestimo najpozneje v 8 dneh. Postopek reševanja pritožb je zaupen.",
          },
          {
            type: "p",
            text: "V skladu s 32. členom Zakona o izvensodnem reševanju potrošniških sporov (ZIsRPS) sporočamo, da ponudnik ne priznava nobenega izvajalca izvensodnega reševanja potrošniških sporov kot pristojnega za reševanje potrošniškega spora. To ne posega v pravico potrošnika, da spor reši po sodni poti ali da se obrne na pristojne organe.",
          },
          {
            type: "p",
            text: "Evropska platforma za spletno reševanje sporov (SRPS) je s 20. julijem 2025 prenehala delovati na podlagi Uredbe (EU) 2024/3228, zato povezave nanjo ne objavljamo več.",
          },
          {
            type: "p",
            text: "Potrošnik se lahko obrne tudi na Tržni inšpektorat Republike Slovenije, Dunajska cesta 160, 1000 Ljubljana, ali na Evropski potrošniški center Slovenija za čezmejne spore znotraj EU.",
          },
        ],
      },
      {
        title: "18. Spremembe pogojev in storitve",
        blocks: [
          {
            type: "p",
            text: "Pogoje lahko spremenimo zaradi sprememb predpisov, sprememb storitve ali odprave nejasnosti. Veljavno različico vedno objavimo na tej strani z datumom zadnje posodobitve.",
          },
          {
            type: "p",
            text: "O bistvenih spremembah registrirane uporabnike obvestimo po elektronski pošti najmanj 15 dni pred uveljavitvijo. Če se s spremembo ne strinjate, lahko pred njeno uveljavitvijo prenehate uporabljati storitev in zahtevate izbris računa; za neporabljene kredite vam v tem primeru povrnemo plačano ceno.",
          },
          {
            type: "p",
            text: "Za že oddana naročila veljajo pogoji, ki so veljali ob oddaji naročila.",
          },
        ],
      },
      {
        title: "19. Veljavno pravo in pristojnost",
        blocks: [
          {
            type: "p",
            text: "Za pogodbena razmerja po teh pogojih se uporablja pravo Republike Slovenije, brez uporabe kolizijskih pravil. Uporaba Konvencije ZN o pogodbah o mednarodni prodaji blaga je izključena.",
          },
          {
            type: "p",
            text: "Za spore je pristojno stvarno pristojno sodišče v Republiki Sloveniji. Pri potrošnikih ta izbira ne odvzame varstva, ki ga potrošniku zagotavljajo prisilni predpisi države njegovega običajnega prebivališča; potrošnik lahko tožbo vloži tudi pri sodišču v državi svojega prebivališča.",
          },
        ],
      },
      {
        title: "20. Končne določbe",
        blocks: [
          {
            type: "p",
            text: "Če je posamezna določba teh pogojev neveljavna ali neizvršljiva, to ne vpliva na veljavnost preostalih določb; neveljavna določba se nadomesti z veljavno, ki je gospodarsko najbližja namenu neveljavne.",
          },
          {
            type: "p",
            text: "Pogoji so objavljeni v slovenskem, angleškem, nemškem in ruskem jeziku. V primeru razhajanja med jezikovnimi različicami je merodajna slovenska različica.",
          },
          {
            type: "p",
            text: "Za vprašanja v zvezi s temi pogoji smo dosegljivi na info@nreport.ai.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  // ---------------------------------------------------------------------------
  // Politika zasebnosti
  // ---------------------------------------------------------------------------
  privacy: {
    eyebrow: legalEyebrow,
    title: "Politika zasebnosti",
    updated,
    intro:
      "Ta politika pojasnjuje, katere osebne podatke družba Noventis d.o.o. obdeluje pri upravljanju spletnega mesta nreport.ai in izvajanju storitve nReport, zakaj jih obdeluje, na kateri pravni podlagi, komu jih posreduje, kako dolgo jih hrani in katere pravice imate v zvezi z njimi. Pripravljena je v skladu z Uredbo (EU) 2016/679 (GDPR) in Zakonom o varstvu osebnih podatkov (ZVOP-2).",
    sections: [
      {
        title: "1. Upravljavec in kontaktni podatki",
        blocks: [
          { type: "p", text: "Upravljavec osebnih podatkov je:" },
          { type: "company" },
          {
            type: "p",
            text: "Za vsa vprašanja o varstvu osebnih podatkov in za uveljavljanje pravic pišite na privacy@nreport.ai ali na naslov sedeža družbe.",
          },
          {
            type: "p",
            text: "Glede na naravo, obseg in namene obdelave upravljavec ni zavezan imenovati pooblaščene osebe za varstvo podatkov (37. člen GDPR). Če se to spremeni, bomo kontaktne podatke objavili tukaj.",
          },
        ],
      },
      {
        title: "2. Katere podatke obdelujemo",
        blocks: [
          {
            type: "table",
            head: ["Kategorija", "Konkretni podatki", "Izvor"],
            rows: [
              [
                "Podatki o računu",
                "elektronski naslov, ime (če ga navedete), geslo v zgoščeni obliki, identifikator ponudnika prijave (Google, Facebook), datum registracije, jezik",
                "od vas oziroma od ponudnika prijave",
              ],
              [
                "Podatki o naročilu",
                "naslov in identifikacija nepremičnine, ki jo poizvedujete, številka stanovanja, vrsta nepremičnine, izdelana poročila, stanje kreditov, zgodovina porabe, čas prvega odprtja poročila, prenosa dokumenta PDF in ogleda pogajalskega lista",
                "od vas in iz delovanja storitve",
              ],
              [
                "Plačilni podatki",
                "znesek, valuta, čas, status plačila, identifikator seje pri ponudniku plačil, podatki za račun. Podatkov o plačilni kartici ne prejmemo in ne hranimo.",
                "od ponudnika plačilnih storitev Stripe",
              ],
              [
                "Tehnični podatki",
                "naslov IP, podatki o brskalniku in napravi, čas dostopa, zahtevani naslovi URL, viri napak",
                "samodejno ob uporabi",
              ],
              [
                "Podatki o privolitvah",
                "izbira glede piškotkov in čas te izbire, privolitev za e-novice, soglasje k takojšnji izvedbi pri nakupu",
                "od vas",
              ],
              [
                "Komunikacija",
                "vsebina sporočil, ki nam jih pošljete, in naši odgovori",
                "od vas",
              ],
              [
                "Podatki o rabi in trženju",
                "dogodki v aplikaciji, viri obiska, konverzije — samo v obsegu, ki ste ga dovolili s privolitvijo za piškotke",
                "samodejno, ob privolitvi",
              ],
            ],
          },
          {
            type: "p",
            text: "Posebnih vrst osebnih podatkov iz 9. člena GDPR (na primer podatkov o zdravju, veri ali političnem prepričanju) ne zbiramo in vas prosimo, da nam jih ne pošiljate.",
          },
        ],
      },
      {
        title: "3. Nameni in pravne podlage obdelave",
        blocks: [
          {
            type: "table",
            head: ["Namen", "Pravna podlaga", "Rok hrambe"],
            rows: [
              [
                "Vodenje uporabniškega računa in prijava",
                "izvajanje pogodbe — člen 6(1)(b) GDPR",
                "do izbrisa računa; nato do 30 dni v varnostnih kopijah",
              ],
              [
                "Izdelava in dostava naročenega poročila",
                "izvajanje pogodbe — člen 6(1)(b) GDPR",
                "za čas trajanja računa, da poročila ostanejo dostopna",
              ],
              [
                "Obdelava plačil in preprečevanje goljufij",
                "izvajanje pogodbe — člen 6(1)(b); zakonita obveznost — člen 6(1)(c); zakoniti interes — člen 6(1)(f)",
                "po pravilih o hrambi računovodske dokumentacije",
              ],
              [
                "Izdaja in hramba računov, davčne obveznosti",
                "zakonita obveznost — člen 6(1)(c) GDPR (ZDDV-1, ZGD-1)",
                "10 let od konca leta, na katero se račun nanaša",
              ],
              [
                "Transakcijska obvestila (potrditev naročila, poročilo je pripravljeno, ponastavitev gesla)",
                "izvajanje pogodbe — člen 6(1)(b) GDPR",
                "do izbrisa računa",
              ],
              [
                "Podpora uporabnikom in reševanje pritožb",
                "izvajanje pogodbe — člen 6(1)(b); zakoniti interes — člen 6(1)(f)",
                "3 leta od zaključka zadeve",
              ],
              [
                "Varnost sistema, dnevniki, preprečevanje zlorab",
                "zakoniti interes — člen 6(1)(f) GDPR",
                "praviloma 90 dni; dlje samo pri raziskavi konkretnega incidenta",
              ],
              [
                "Merjenje obiska in izboljševanje storitve (analitika)",
                "privolitev — člen 6(1)(a) GDPR in 225. člen ZEKom-2",
                "do preklica privolitve; piškotki po rokih iz Politike piškotkov",
              ],
              [
                "Oglaševanje in merjenje uspešnosti oglasov",
                "privolitev — člen 6(1)(a) GDPR in 225. člen ZEKom-2",
                "do preklica privolitve",
              ],
              [
                "Pošiljanje e-novic",
                "privolitev — člen 6(1)(a) GDPR",
                "do odjave oziroma preklica privolitve",
              ],
              [
                "Uveljavljanje ali obramba pravnih zahtevkov",
                "zakoniti interes — člen 6(1)(f) GDPR",
                "do izteka zastaralnih rokov",
              ],
            ],
          },
          {
            type: "p",
            text: "Kadar se opiramo na zakoniti interes, smo opravili tehtanje med tem interesom in vašimi pravicami. Povzetek tehtanja vam na zahtevo posredujemo na privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "4. Podatki o nepremičnini in podatki drugih oseb",
        blocks: [
          {
            type: "p",
            text: "Naslov nepremičnine, ki jo poizvedujete, je za nas podatek o naročilu. Kadar je nepremičnina povezana z vami (na primer vaše prebivališče), gre tudi za vaš osebni podatek in ga obravnavamo enako kot ostale podatke o naročilu.",
          },
          {
            type: "p",
            text: "Poročilo lahko vsebuje podatke iz javnih evidenc, ki se nanašajo na tretje osebe — zlasti kadar je za nepremičnino dosegljiv zemljiškoknjižni izpisek, ki izkazuje lastništvo ali bremena. Te podatke obdelujemo izključno zato, da izdelamo poročilo, ki ste ga naročili, na podlagi zakonitega interesa iz člena 6(1)(f) GDPR, v obsegu, v katerem so v javni evidenci že javno dostopni.",
          },
          {
            type: "p",
            text: "Iz teh podatkov ne oblikujemo profilov posameznikov, ne gradimo ločene zbirke podatkov o lastnikih in jih ne uporabljamo za trženje. Ker podatkov ne pridobimo neposredno od posameznika in bi bilo obveščanje vsakega posameznika povezano z nesorazmernim naporom, se pri obveščanju opiramo na izjemo iz člena 14(5)(b) GDPR; namesto individualnega obvestila objavljamo to politiko.",
          },
          {
            type: "note",
            text: "Kot naročnik ste odgovorni, da imate za poizvedbo o konkretni nepremičnini zakonit interes (na primer nameravan nakup, najem, financiranje, cenitev ali lastništvo). Storitve ni dovoljeno uporabljati za sledenje, nadlegovanje ali profiliranje posameznikov.",
          },
        ],
      },
      {
        title: "5. Viri podatkov, ki niso osebni podatki uporabnika",
        blocks: [
          {
            type: "p",
            text: "Za izdelavo poročil uporabljamo javno dostopne vire: evidence Geodetske uprave Republike Slovenije (kataster nepremičnin, register nepremičnin, Evidenca trga nepremičnin), zemljiško knjigo, register energetskih izkaznic, kartografske vire ter javno objavljene nepremičninske oglase. Ti viri niso pod našim nadzorom in zanje veljajo pogoji njihovih upravljavcev.",
          },
        ],
      },
      {
        title: "6. Piškotki in podobne tehnologije",
        blocks: [
          {
            type: "p",
            text: "Nenujnih piškotkov in podobnih tehnologij ne naložimo, dokler v to ne privolite. Ob prvem obisku vam prikažemo obvestilo, v katerem lahko privolitev sprejmete v celoti, jo zavrnete ali izberete posamezne kategorije. Zavrnitev je enako preprosta kot privolitev.",
          },
          {
            type: "p",
            text: "Za prijavo in delovanje storitve uporabljamo lokalno shrambo brskalnika (local storage); to je nujno za izvedbo storitve, ki ste jo izrecno zahtevali, zato zanjo privolitev ni potrebna (drugi odstavek 225. člena ZEKom-2).",
          },
          { type: "p", text: "Podroben seznam je v Politiki piškotkov." },
          { type: "cookieSettings", label: "Nastavitve piškotkov" },
        ],
      },
      {
        title: "7. Komu posredujemo podatke",
        blocks: [
          {
            type: "p",
            text: "Osebnih podatkov ne prodajamo. Posredujemo jih le pogodbenim obdelovalcem, ki jih obdelujejo po naših navodilih in na podlagi pogodbe o obdelavi (28. člen GDPR), ter organom, kadar to zahteva zakon.",
          },
          {
            type: "table",
            head: ["Prejemnik", "Namen", "Lokacija obdelave", "Podlaga za prenos"],
            rows: [
              [
                "Supabase, Inc.",
                "podatkovna baza, prijava uporabnikov, shramba datotek, strežniške funkcije",
                "EU (Frankfurt, eu-central-1); podjetje v ZDA",
                "pogodba o obdelavi in standardna pogodbena določila",
              ],
              [
                "Railway Corp.",
                "gostovanje zalednega strežnika, čakalne vrste in izdelava dokumentov PDF",
                "ZDA / EU",
                "standardna pogodbena določila",
              ],
              [
                "Vercel Inc.",
                "gostovanje in dostava spletnega mesta",
                "EU / ZDA",
                "standardna pogodbena določila, okvir EU-ZDA za zasebnost podatkov",
              ],
              [
                "Cloudflare, Inc.",
                "omrežni posrednik, zaščita pred zlorabami in napadi",
                "globalno omrežje",
                "standardna pogodbena določila, okvir EU-ZDA za zasebnost podatkov",
              ],
              [
                "Stripe Payments Europe, Ltd.",
                "izvedba plačil, preprečevanje goljufij (za ta namen samostojni upravljavec)",
                "Irska, ZDA",
                "standardna pogodbena določila",
              ],
              [
                "Anthropic, PBC",
                "generiranje besedilnih delov poročila z jezikovnim modelom",
                "ZDA",
                "standardna pogodbena določila; brez uporabe podatkov za učenje modelov",
              ],
              [
                "Google Ireland Ltd. / Google LLC",
                "zemljevidi, iskanje naslovov in geokodiranje; jezikovni model Gemini; Google Analytics (samo ob privolitvi)",
                "Irska, ZDA",
                "standardna pogodbena določila, okvir EU-ZDA za zasebnost podatkov",
              ],
              [
                "Meta Platforms Ireland Ltd.",
                "merjenje uspešnosti oglaševanja (samo ob privolitvi za trženje)",
                "Irska, ZDA",
                "standardna pogodbena določila, okvir EU-ZDA za zasebnost podatkov",
              ],
              [
                "Resend, Inc.",
                "pošiljanje transakcijske elektronske pošte",
                "ZDA",
                "standardna pogodbena določila",
              ],
              [
                "MailerLite Limited",
                "pošiljanje e-novic (samo ob privolitvi)",
                "Evropska unija",
                "prenosa v tretjo državo ni",
              ],
            ],
          },
          {
            type: "p",
            text: "Podatke lahko razkrijemo tudi sodišču, davčnemu ali nadzornemu organu, policiji ali drugemu pristojnemu organu, kadar smo k temu zavezani, ter našemu računovodskemu servisu, odvetniku ali revizorju v obsegu, potrebnem za njihovo delo. Ob morebitnem statusnem preoblikovanju ali prodaji dejavnosti se podatki lahko prenesejo na naslednika, o čemer vas obvestimo vnaprej.",
          },
        ],
      },
      {
        title: "8. Prenosi v tretje države",
        blocks: [
          {
            type: "p",
            text: "Nekateri obdelovalci imajo sedež ali podporne enote v Združenih državah Amerike. V teh primerih prenos temelji na sklepu Evropske komisije o ustreznosti za okvir EU-ZDA za zasebnost podatkov (kadar je prejemnik certificiran) ali na standardnih pogodbenih določilih Evropske komisije, dopolnjenih z oceno vpliva prenosa in dodatnimi tehničnimi ukrepi, kot sta šifriranje med prenosom in mirovanjem ter omejevanje dostopa.",
          },
          {
            type: "p",
            text: "Kopijo uporabljenih zaščitnih ukrepov lahko zahtevate na privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "9. Samodejno sprejemanje odločitev in profiliranje",
        blocks: [
          {
            type: "p",
            text: "Poročilo, vključno z okvirno oceno vrednosti nepremičnine, nastane samodejno, brez človeškega pregleda. Ta obdelava se nanaša na nepremičnino in ne ustvarja pravnih učinkov za posameznika niti nanj podobno znatno ne vpliva, zato ne gre za samodejno sprejemanje odločitev v smislu prvega odstavka 22. člena GDPR.",
          },
          {
            type: "p",
            text: "Vaših osebnih podatkov ne uporabljamo za samodejno ocenjevanje kreditne sposobnosti, tveganja ali podobno razvrščanje posameznikov.",
          },
        ],
      },
      {
        title: "10. Varnost obdelave",
        blocks: [
          {
            type: "ul",
            items: [
              "šifriranje prenosa (TLS) in šifriranje podatkov v mirovanju,",
              "omejevanje dostopa po načelu najmanjših potrebnih pravic in ločeni administratorski dostopi,",
              "varnost na ravni vrstic v podatkovni bazi, tako da uporabnik dostopa le do svojih zapisov,",
              "beleženje administrativnih posegov,",
              "redne varnostne kopije in preverjanje njihove obnovljivosti,",
              "pogodbe o obdelavi in preverjanje obdelovalcev.",
            ],
          },
          {
            type: "p",
            text: "Če pride do kršitve varstva osebnih podatkov, ki bi verjetno povzročila veliko tveganje za vaše pravice in svoboščine, vas o tem obvestimo brez nepotrebnega odlašanja, o kršitvi pa v 72 urah obvestimo Informacijskega pooblaščenca.",
          },
        ],
      },
      {
        title: "11. Vaše pravice",
        blocks: [
          { type: "p", text: "V zvezi s svojimi osebnimi podatki imate pravico do:" },
          {
            type: "ul",
            items: [
              "dostopa do podatkov in kopije podatkov (15. člen GDPR),",
              "popravka netočnih in dopolnitve nepopolnih podatkov (16. člen),",
              "izbrisa podatkov oziroma »pravice do pozabe« (17. člen),",
              "omejitve obdelave (18. člen),",
              "prenosljivosti podatkov v strojno berljivi obliki (20. člen),",
              "ugovora obdelavi, ki temelji na zakonitem interesu, in kadar koli ugovora obdelavi za neposredno trženje (21. člen),",
              "preklica privolitve kadar koli, ne da bi to vplivalo na zakonitost obdelave pred preklicem (7. člen).",
            ],
          },
          {
            type: "p",
            text: "Zahtevo pošljite na privacy@nreport.ai. Odgovorimo najpozneje v enem mesecu od prejema; rok lahko ob zapleteni zahtevi podaljšamo za dva meseca, o čemer vas obvestimo. Uveljavljanje pravic je brezplačno; pri očitno neutemeljenih ali pretiranih zahtevah lahko zaračunamo razumno pristojbino ali zahtevo zavrnemo. Zaradi varnosti lahko zahtevamo dodatne podatke za potrditev vaše istovetnosti.",
          },
          {
            type: "p",
            text: "Brisanje računa in podatkov lahko zahtevate tudi prek namenskega obrazca na spletnem mestu. Del podatkov moramo kljub zahtevi za izbris hraniti, kadar to zahteva zakon — zlasti izdane račune za obdobje davčne hrambe.",
          },
          {
            type: "p",
            text: "Če menite, da obdelava vaših osebnih podatkov krši predpise, imate pravico do pritožbe pri nadzornem organu:",
          },
          {
            type: "links",
            items: [
              {
                label: "Informacijski pooblaščenec RS, Dunajska cesta 22, 1000 Ljubljana — www.ip-rs.si",
                href: "https://www.ip-rs.si/",
              },
            ],
          },
          {
            type: "p",
            text: "Pritožbo lahko vložite tudi pri nadzornem organu v državi svojega običajnega prebivališča ali kraja domnevne kršitve.",
          },
        ],
      },
      {
        title: "12. Ali je posredovanje podatkov obvezno",
        blocks: [
          {
            type: "p",
            text: "Posredovanje elektronskega naslova in podatkov o nepremičnini je pogoj za sklenitev in izvedbo pogodbe — brez njih poročila ne moremo izdelati in dostaviti. Posredovanje podatkov za e-novice in privolitev za nenujne piškotke sta povsem prostovoljna; njuna zavrnitev ne vpliva na uporabo storitve.",
          },
        ],
      },
      {
        title: "13. Otroci",
        blocks: [
          {
            type: "p",
            text: "Storitev ni namenjena osebam, mlajšim od 18 let, in podatkov o njih zavestno ne zbiramo. Če ugotovimo, da smo obdelali podatke mladoletne osebe brez ustrezne podlage, jih izbrišemo. Če menite, da se je to zgodilo, nas obvestite na privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "14. Povezave na druga spletna mesta",
        blocks: [
          {
            type: "p",
            text: "Spletno mesto vsebuje povezave na strani tretjih oseb (na primer na uradne evidence ali oglasnike). Za njihovo vsebino in ravnanje z osebnimi podatki ne odgovarjamo; priporočamo, da preberete njihove politike zasebnosti.",
          },
        ],
      },
      {
        title: "15. Spremembe politike",
        blocks: [
          {
            type: "p",
            text: "Politiko lahko posodobimo ob spremembi storitve, obdelovalcev ali predpisov. Vsaka različica nosi datum zadnje posodobitve. O bistvenih spremembah registrirane uporabnike obvestimo po elektronski pošti ali z obvestilom na spletnem mestu; kadar sprememba zahteva vašo privolitev, jo bomo posebej pridobili.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  // ---------------------------------------------------------------------------
  // Politika piškotkov
  // ---------------------------------------------------------------------------
  cookies: {
    eyebrow: legalEyebrow,
    title: "Politika piškotkov",
    updated,
    intro:
      "Ta politika pojasnjuje, katere piškotke in podobne tehnologije uporablja spletno mesto nreport.ai, ki ga upravlja družba Noventis d.o.o., zakaj jih uporablja in kako lahko svojo izbiro kadar koli spremenite.",
    sections: [
      {
        title: "1. Kaj so piškotki",
        blocks: [
          {
            type: "p",
            text: "Piškotki so majhne besedilne datoteke, ki jih spletno mesto shrani v vaš brskalnik. Podobno vlogo imajo lokalna shramba (local storage), shramba seje (session storage) in slikovne sledilne pike. V tej politiki jih skupaj imenujemo »piškotki«.",
          },
          {
            type: "p",
            text: "Piškotki omogočajo, da si spletno mesto zapomni vašo prijavo in nastavitve, nekateri pa se uporabljajo tudi za merjenje obiska in oglaševanje.",
          },
        ],
      },
      {
        title: "2. Pravna podlaga in vaša privolitev",
        blocks: [
          {
            type: "p",
            text: "V skladu s prvim odstavkom 225. člena Zakona o elektronskih komunikacijah (ZEKom-2) in členom 6(1)(a) GDPR nenujnih piškotkov ne naložimo in ne beremo, dokler v to ne privolite. Privolitev je prostovoljna, določna in nedvoumna, zavrnitev pa je enako preprosta kot sprejem.",
          },
          {
            type: "p",
            text: "Za nujno potrebne piškotke privolitev ni potrebna, ker so nujni za izvedbo storitve, ki ste jo izrecno zahtevali (drugi odstavek 225. člena ZEKom-2).",
          },
          {
            type: "p",
            text: "Privolitev lahko kadar koli prekličete ali spremenite. Preklic ne vpliva na zakonitost obdelave pred preklicem.",
          },
          { type: "cookieSettings", label: "Nastavitve piškotkov" },
        ],
      },
      {
        title: "3. Kategorije piškotkov, ki jih uporabljamo",
        subsections: [
          {
            title: "Nujno potrebni (vedno aktivni)",
            blocks: [
              {
                type: "p",
                text: "Omogočajo prijavo, ohranjanje seje, varnost obrazcev in izvedbo plačila. Brez njih spletno mesto ne deluje. Zanje ne potrebujemo vaše privolitve, izklopiti pa jih ni mogoče.",
              },
              {
                type: "table",
                head: ["Ime / tehnologija", "Ponudnik", "Namen", "Trajanje"],
                rows: [
                  [
                    "sb-…-auth-token (lokalna shramba)",
                    "nReport / Supabase",
                    "ohranjanje prijave uporabnika",
                    "do odjave ali izbrisa podatkov brskalnika",
                  ],
                  [
                    "nreport-cookie-consent (lokalna shramba)",
                    "nReport",
                    "shranjevanje vaše izbire glede piškotkov",
                    "do preklica ali izbrisa podatkov brskalnika",
                  ],
                  [
                    "selectedAddress, selectedPropertyType in podobno (lokalna shramba)",
                    "nReport",
                    "ohranjanje vnesenih podatkov med koraki naročila",
                    "do zaključka naročila",
                  ],
                  [
                    "metaPurchaseContext (shramba seje)",
                    "nReport",
                    "prenos podatkov o nakupu na stran s potrditvijo",
                    "do zaprtja zavihka",
                  ],
                  [
                    "__stripe_mid, __stripe_sid",
                    "Stripe",
                    "izvedba plačila in preprečevanje goljufij; naložijo se šele na strani za plačilo",
                    "1 leto / 30 minut",
                  ],
                ],
              },
            ],
          },
          {
            title: "Analitični (samo z vašo privolitvijo)",
            blocks: [
              {
                type: "p",
                text: "Pomagajo nam razumeti, koliko obiskovalcev ima spletno mesto, katere strani obiskujejo in kje naletijo na težave. Podatke uporabljamo za izboljševanje storitve.",
              },
              {
                type: "table",
                head: ["Ime", "Ponudnik", "Namen", "Trajanje"],
                rows: [
                  [
                    "_ga, _ga_*",
                    "Google Analytics 4 (Google Ireland Ltd.)",
                    "razlikovanje obiskovalcev in merjenje obiska",
                    "do 2 leti",
                  ],
                  [
                    "_gid, _gat",
                    "Google Analytics 4",
                    "razlikovanje obiskovalcev, omejevanje pogostosti zahtev",
                    "24 ur / 1 minuta",
                  ],
                ],
              },
              {
                type: "p",
                text: "Google Analytics uporabljamo z nastavitvijo Consent Mode; brez vaše privolitve se skripta sploh ne naloži, funkciji Google Signals in personalizacija oglasov pa sta izklopljeni.",
              },
            ],
          },
          {
            title: "Trženjski (samo z vašo privolitvijo)",
            blocks: [
              {
                type: "p",
                text: "Omogočajo merjenje uspešnosti naših oglasnih kampanj in prikaz ustreznejših oglasov na platformah družbe Meta.",
              },
              {
                type: "table",
                head: ["Ime", "Ponudnik", "Namen", "Trajanje"],
                rows: [
                  [
                    "_fbp",
                    "Meta Platforms Ireland Ltd.",
                    "prepoznavanje brskalnika za merjenje konverzij",
                    "do 3 mesece",
                  ],
                  [
                    "_fbc",
                    "Meta Platforms Ireland Ltd.",
                    "beleženje klika na oglas, s katerim ste prišli na stran",
                    "do 3 mesece",
                  ],
                ],
              },
              {
                type: "p",
                text: "Če trženjskih piškotkov ne dovolite, ne naložimo slikovne pike Meta in tudi strežniško ne poročamo o vašem nakupu družbi Meta.",
              },
            ],
          },
        ],
      },
      {
        title: "4. Kako spremenite ali prekličete privolitev",
        blocks: [
          {
            type: "p",
            text: "Svojo izbiro lahko kadar koli spremenite s klikom na spodnji gumb ali na povezavo »Nastavitve piškotkov« v nogi spletnega mesta.",
          },
          { type: "cookieSettings", label: "Nastavitve piškotkov" },
          {
            type: "p",
            text: "Ob preklicu privolitve prenehamo nalagati zadevne skripte in izbrišemo piškotke, ki jih lahko odstranimo. Piškotke, ki so že shranjeni v vašem brskalniku, lahko odstranite tudi sami v nastavitvah brskalnika.",
          },
        ],
      },
      {
        title: "5. Upravljanje piškotkov v brskalniku",
        blocks: [
          {
            type: "ul",
            items: [
              "Chrome: Nastavitve › Zasebnost in varnost › Piškotki tretjih oseb",
              "Firefox: Nastavitve › Zasebnost in varnost › Piškotki in podatki spletnih mest",
              "Safari: Nastavitve › Zasebnost › Upravljanje podatkov spletnih mest",
              "Edge: Nastavitve › Piškotki in dovoljenja spletnih mest",
            ],
          },
          {
            type: "p",
            text: "Blokiranje vseh piškotkov lahko onemogoči prijavo in nakup, ker spletno mesto ne more ohraniti vaše seje.",
          },
        ],
      },
      {
        title: "6. Politike zunanjih ponudnikov",
        blocks: [
          {
            type: "links",
            items: [
              { label: "Google — Politika zasebnosti", href: "https://policies.google.com/privacy" },
              {
                label: "Meta — Politika zasebnosti",
                href: "https://www.facebook.com/privacy/policy",
              },
              { label: "Stripe — Politika zasebnosti", href: "https://stripe.com/privacy" },
              { label: "Supabase — Politika zasebnosti", href: "https://supabase.com/privacy" },
            ],
          },
        ],
      },
      {
        title: "7. Spremembe in kontakt",
        blocks: [
          {
            type: "p",
            text: "Politiko piškotkov posodabljamo ob spremembi uporabljenih tehnologij. Kadar se nabor piškotkov bistveno spremeni, vas znova povprašamo za privolitev.",
          },
          {
            type: "p",
            text: "Za vprašanja o piškotkih pišite na privacy@nreport.ai. Več o obdelavi osebnih podatkov je v Politiki zasebnosti.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  // ---------------------------------------------------------------------------
  // Obrazec za brisanje podatkov (ni pravni dokument — besedilo strani)
  // ---------------------------------------------------------------------------
  dataDeletion: {
    title: "Brisanje osebnih podatkov",
    subtitle:
      "Zahtevajte izbris svojih osebnih podatkov v skladu s 17. členom Splošne uredbe o varstvu podatkov (GDPR).",
    alert:
      "Pomembno: brisanje je nepreklicno. Po izbrisu ne boste več mogli dostopati do svojega računa, kupljenih kreditov in izdelanih poročil.",
    formTitle: "Zahteva za brisanje podatkov",
    formDesc: "Izpolnite spodnji obrazec za zahtevo za izbris osebnih podatkov.",
    emailLabel: "E-poštni naslov *",
    emailPlaceholder: "vas.email@primer.si",
    emailHint: "Vnesite e-poštni naslov, povezan z vašim računom.",
    reasonLabel: "Razlog za izbris (neobvezno)",
    reasonPlaceholder: "Opišite razlog za izbris podatkov...",
    deletedDataTitle: "Podatki, ki bodo izbrisani:",
    deletedDataItems: [
      "Podatki o računu (e-pošta, ime, nastavitve)",
      "Zgodovina naročil in poraba kreditov",
      "Shranjena poročila in analize",
      "Naložene datoteke in priloge",
      "Podatki o privolitvah in preferencah",
    ],
    submit: "Pošlji zahtevo za izbris",
    contact: "Imate vprašanja? Kontaktirajte nas na",
    successTitle: "Zahteva uspešno poslana",
    successDesc:
      "Vašo zahtevo smo prejeli. Obdelali jo bomo najpozneje v enem mesecu od prejema, kot določa 12. člen GDPR.",
    processingTitle: "Čas obdelave",
    processingDesc:
      "Zahtevo obdelamo najpozneje v enem mesecu. Izdane račune moramo zaradi davčnih predpisov hraniti 10 let, zato ti ostanejo shranjeni tudi po izbrisu računa.",
    securityTitle: "Varnost podatkov",
    securityDesc:
      "Pred izbrisom lahko zahtevamo potrditev vaše istovetnosti. Podatki se varno izbrišejo iz naših sistemov, iz varnostnih kopij pa najpozneje v 30 dneh.",
    backHome: "Nazaj na domačo stran",
    toastEmailRequired: "Prosimo, vnesite svoj e-poštni naslov.",
    toastSendFailed: "Zahteve ni bilo mogoče poslati. Poskusite znova ali nam pišite na info@nreport.ai.",
    toastSuccessTitle: "Zahteva poslana",
    toastSuccessDesc: "Vaša zahteva za izbris podatkov je bila uspešno poslana.",
  },
};
